import React from "react";
import {
  Card,
  Table,
  Spinner,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";

import {
  FiEye,
  FiShoppingBag,
  FiPackage,
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";

import styles from "./ProductTableList.module.css";
import CommonActionMenu from "../../Common/CommonAction/CommonActionMenu";

/* ---------------- DUMMY DATA ---------------- */

const dummyProducts = [
  {
    id: 1,
    image:
      "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=200",
    title:
      "Premium Smart Watch Series 9 With AMOLED Display & Health Tracking Features",
    sku: "SW-1001",
    product_code: "PRD-1001",
    brand: "Apple",
    category: "Electronics",
    sub_category: "Smart Watch",
    sale_count: 1245,
    view_count: 12890,
  },
  {
    id: 2,
    image:
      "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=200",
    title:
      "Nike Air Max Running Shoes For Men Lightweight Sports Sneakers",
    sku: "NK-2201",
    product_code: "PRD-1002",
    brand: "Nike Sports International Brand",
    category: "Footwear",
    sub_category: "Sports Shoes",
    sale_count: 24500,
    view_count: 145600,
  },
  {
    id: 3,
    image:
      "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=200",
    title:
      "Oversized Cotton Printed T-Shirt For Casual Daily Wear",
    sku: "TS-3301",
    product_code: "PRD-1003",
    brand: "H&M Fashion Collection",
    category: "Fashion",
    sub_category: "T-Shirts",
    sale_count: 980,
    view_count: 2500,
  },
  {
    id: 4,
    image:
      "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=200",
    title:
      "Wireless Bluetooth Headphones With Deep Bass Sound",
    sku: "HP-4501",
    product_code: "PRD-1004",
    brand: "Sony Audio Premium",
    category: "Electronics",
    sub_category: "Headphones",
    sale_count: 125000,
    view_count: 456000,
  },
  {
    id: 5,
    image:
      "https://images.unsplash.com/photo-1512436991641-6745cdb1723f?w=200",
    title:
      "Women Stylish Handbag With Premium Leather Finish",
    sku: "BG-5501",
    product_code: "PRD-1005",
    brand: "Zara Fashion Accessories",
    category: "Accessories",
    sub_category: "Hand Bags",
    sale_count: 5400,
    view_count: 11200,
  },
];

/* ---------------- FORMAT COUNTS ---------------- */

const formatCount = (num) => {
  if (!num) return "0";

  if (num >= 10000000) {
    return `${(num / 10000000)
      .toFixed(1)
      .replace(".0", "")}Cr`;
  }

  if (num >= 100000) {
    return `${(num / 100000)
      .toFixed(1)
      .replace(".0", "")}L`;
  }

  if (num >= 1000) {
    return `${(num / 1000)
      .toFixed(1)
      .replace(".0", "")}k`;
  }

  return num;
};

const ProductTableList = ({
  data = dummyProducts,
  loading = false,
  onEdit,
  onDelete,
  actionLoading,
}) => {
  return (
    <Card className={styles.mainCard}>
      <Card.Body className={styles.cardBody}>
        {loading ? (
          <div className={styles.loaderContainer}>
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <Table
              responsive
              borderless
              className={styles.table}
            >
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Code</th>
                  <th>Brand</th>
                  <th>Category</th>
                  <th>Sales</th>
                  <th>Views</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {data?.length > 0 ? (
                  data?.map((item, index) => (
                    <tr
                      key={item?.id || index}
                      className={`${styles.row} ${
                        index % 2 === 0
                          ? styles.evenRow
                          : styles.oddRow
                      }`}
                    >
                      {/* PRODUCT */}
                      <td className={styles.productTd}>
                        <div className={styles.productBox}>
                          <img
                            src={
                              item?.image ||
                              "https://via.placeholder.com/60"
                            }
                            alt="product"
                            className={styles.productImg}
                          />

                          <div className={styles.productInfo}>
                            <div
                              className={styles.productTitle}
                              title={item?.title}
                            >
                              {item?.title || "No Title"}
                            </div>

                            <div className={styles.productSub}>
                              <FiPackage size={10} />
                              SKU: {item?.sku || "NA"}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* PRODUCT CODE */}
                      <td>
                        <div className={styles.code}>
                          {item?.product_code || "NA"}
                        </div>
                      </td>

                      {/* BRAND */}
                      <td className={styles.brandTd}>
                        <span
                          bg="light"
                          text="dark"
                          className={styles.badge}
                        >
                          {item?.brand || "NA"}
                        </span>
                      </td>

                      {/* CATEGORY */}
                      <td>
                        <div className={styles.category}>
                          {item?.category || "NA"}

                          <span>
                            / {item?.sub_category || "NA"}
                          </span>
                        </div>
                      </td>

                      {/* SALES */}
                      <td>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>
                              {item?.sale_count}
                            </Tooltip>
                          }
                        >
                          <div className={styles.countBox}>
                            <FiShoppingBag />
                            {formatCount(
                              item?.sale_count
                            )}
                          </div>
                        </OverlayTrigger>
                      </td>

                      {/* VIEWS */}
                      <td>
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>
                              {item?.view_count}
                            </Tooltip>
                          }
                        >
                          <div className={styles.countBox}>
                            <FiEye />
                            {formatCount(
                              item?.view_count
                            )}
                          </div>
                        </OverlayTrigger>
                      </td>

                      {/* ACTION */}
                      <td className={styles.actionCell}>
                        {parseInt(actionLoading?.delId) ===
                        parseInt(item?.id) ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Deleting...
                          </>
                        ) : (
                          <CommonActionMenu
                            actions={[
                              {
                                label: "Edit",
                                icon: <FiEdit2 />,
                                onClick: () =>
                                  onEdit?.(item),
                              },
                              {
                                label: "Delete",
                                icon: <FiTrash2 />,
                                danger: true,
                                onClick: () => {
                                  onDelete?.(item);
                                },
                              },
                            ]}
                          />
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      className={styles.emptyState}
                    >
                      No products found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ProductTableList;